// Central SEO configuration for Ministry of Jesus website.
// All page-level <Seo> components and JSON-LD builders pull defaults from here.
//
// IMPORTANT: SITE_URL is a placeholder until production domain is confirmed.
// Once the live domain is provisioned, only this constant needs to change for
// canonical URLs, sitemap.xml, robots.txt and Open Graph tags to update.

export const SITE_URL = 'https://www.ministryofjesus.ie';

export const SITE_NAME = 'Ministry of Jesus';
export const SITE_LOCALE = 'en_IE';
export const SITE_LANG = 'en-IE';

// Brand logo used in OG previews and Schema.org image fields
export const SITE_LOGO = 'https://customer-assets.emergentagent.com/job_pentecost-portal/artifacts/yi2c4y36_MoJ_LOGO_Web.png';

// Default social share image (16:9 preferred for OG cards)
export const SITE_OG_IMAGE = SITE_LOGO;

export const SITE_TWITTER = '@MinistryofJesus';

// Local SEO — counties targeted for organic discovery
export const TARGET_COUNTIES = ['Dublin', 'Kildare', 'Meath', 'Wicklow'];

// Single source of truth for NAP (Name, Address, Phone) used in JSON-LD
export const BUSINESS = {
  name: 'Ministry of Jesus',
  legalName: 'Ministry of Jesus — Pentecostal Church Ireland',
  description:
    'Ministry of Jesus is a Spirit-filled Pentecostal church in Dublin, Ireland, serving worshippers across Dublin, Kildare, Meath and Wicklow. Join us for Sunday Worship, Sunday School, Daily Devotion, Healing & Deliverance Prayer and Early Morning Prayer.',
  email: 'info@ministryofjesus.ie',
  phone: '+353 87 231 9966',
  // Postal address (ISO + PostalAddress schema)
  address: {
    streetAddress: '7 Le Fanu Road, Ballyfermot Road, Cherry Orchard',
    addressLocality: 'Dublin',
    addressRegion: 'County Dublin',
    postalCode: 'D10 NH74',
    addressCountry: 'IE',
  },
  // Approximate geo for Cherry Orchard / Ballyfermot, Dublin 10
  geo: { latitude: 53.3425, longitude: -6.3608 },
  areaServed: [
    { name: 'Dublin', type: 'AdministrativeArea' },
    { name: 'County Kildare', type: 'AdministrativeArea' },
    { name: 'County Meath', type: 'AdministrativeArea' },
    { name: 'County Wicklow', type: 'AdministrativeArea' },
    { name: 'Ireland', type: 'Country' },
  ],
  founder: 'Pastor Jayaseelan Vincent',
  foundingLocation: 'Dublin, Ireland',
  sameAs: [
    'https://www.youtube.com/@MinistryofJesus',
    'https://www.instagram.com/ministryofjesusireland/',
  ],
  // Weekly recurring services (used in Event schema + opening hours)
  services: [
    {
      name: 'Sunday School',
      day: 'Sunday',
      openTime: '09:30',
      closeTime: '10:30',
      description:
        'Sunday School for children — Bible stories, worship, prayer and crafts in a safe, Garda-vetted environment.',
    },
    {
      name: 'Sunday Worship Service',
      day: 'Sunday',
      openTime: '10:30',
      closeTime: '12:30',
      description:
        'Spirit-filled Pentecostal Sunday Worship Service with praise, the Word of God and prayer ministry.',
    },
    {
      name: 'Deliverance & Healing Prayer',
      day: 'Thursday',
      openTime: '10:00',
      closeTime: '12:00',
      description:
        'Weekly Deliverance and Healing Prayer ministry — bring your prayer requests and receive ministry in Jesus’ name.',
    },
  ],
};

// Keyword pools for natural inclusion in meta descriptions / page copy
export const KEYWORDS = {
  brand: ['Ministry of Jesus', 'Ministry of Jesus Ireland', 'Ministry of Jesus Dublin'],
  category: [
    'Pentecostal church',
    'Spirit-filled church',
    'church in Ireland',
    'Christian church',
    'Bible-believing church',
    'Pentecostal church near me',
  ],
  region: [
    'church in Dublin',
    'church in Ballyfermot',
    'church in Cherry Orchard',
    'church in Kildare',
    'church in Naas',
    'church in Maynooth',
    'church in Meath',
    'church in Navan',
    'church in Wicklow',
    'church in Bray',
    'churches near Dublin',
  ],
  ministry: [
    'Sunday Worship Dublin',
    'Healing prayer Dublin',
    'Deliverance ministry Ireland',
    'Online morning prayer Ireland',
    'Daily devotion YouTube',
    'Vacation Bible School Dublin',
    'Sunday School Dublin',
  ],
};

// Page-level SEO defaults. Each public route maps to a record below.
export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'Ministry of Jesus | Pentecostal Church in Dublin, Ireland',
    description:
      'Ministry of Jesus is a Pentecostal church in Dublin, Ireland serving Dublin, Kildare, Meath and Wicklow. Join Sunday Worship at 10:30, Sunday School at 09:30, Thursday Healing Prayer and Daily Devotion online.',
    keywords: [
      'Pentecostal church Dublin',
      'church in Dublin Ireland',
      'Spirit-filled church Ireland',
      'church near Kildare',
      'church near Meath',
      'church near Wicklow',
      'Ministry of Jesus',
      'Sunday worship Dublin',
    ],
  },
  about: {
    path: '/about',
    title: 'About Our Church | Ministry of Jesus, Dublin',
    description:
      'Discover the vision, mission and beliefs of Ministry of Jesus — a Pentecostal church in Dublin led by Pastor Jayaseelan Vincent, serving believers across Dublin, Kildare, Meath and Wicklow.',
    keywords: ['About Ministry of Jesus', 'Pentecostal beliefs Ireland', 'Pastor Jayaseelan Vincent', 'church in Cherry Orchard Dublin'],
  },
  services: {
    path: '/services',
    title: 'Our Ministries & Services | Ministry of Jesus Dublin',
    description:
      'Explore the ministries of Ministry of Jesus — Sunday School, Summer Camp, Prayer Cell and Evangelism in Dublin, Kildare, Meath and Wicklow.',
    keywords: ['church ministries Dublin', 'Sunday school Dublin', 'prayer cell Ireland', 'evangelism ministry Ireland'],
  },
  sundaySchool: {
    path: '/services/sunday-school',
    title: 'Sunday School for Children | Ministry of Jesus Dublin',
    description:
      'Bible-based Sunday School for children in Dublin every Sunday at 09:30 AM. Safe, Garda-vetted environment serving families across Dublin, Kildare, Meath and Wicklow.',
    keywords: ['Sunday School Dublin', 'Christian children ministry Dublin', 'Bible class for kids Ireland'],
  },
  summerCamp: {
    path: '/services/summer-camp',
    title: 'Summer Camp & Vacation Bible School | Dublin',
    description:
      'Five days of Bible lessons, worship, games and crafts for children ages 5–14. Open to families across Dublin, Kildare, Meath and Wicklow.',
    keywords: ['Summer camp Dublin', 'VBS Ireland', 'Vacation Bible School Dublin', 'Christian summer camp Ireland'],
  },
  prayerCell: {
    path: '/services/prayer-cell',
    title: 'Prayer Cell & Home Groups | Ministry of Jesus Dublin',
    description:
      'Join a Prayer Cell with Ministry of Jesus — worship, intercession and healing prayer in homes and at our Dublin church. Submit a prayer request online.',
    keywords: ['prayer cell Dublin', 'home prayer group Ireland', 'healing prayer Dublin'],
  },
  evangelism: {
    path: '/services/evangelism',
    title: 'Evangelism Ministry | Ministry of Jesus, Dublin',
    description:
      'Carrying the gospel of Jesus Christ into Dublin and surrounding counties through street prayer, testimony and community service.',
    keywords: ['evangelism Dublin', 'street ministry Ireland', 'Christian outreach Dublin'],
  },
  live: {
    path: '/live',
    title: 'Watch LIVE | Ministry of Jesus Sunday Worship Online',
    description:
      'Watch Ministry of Jesus Sunday Worship LIVE from Dublin every Sunday at 10:30. Join believers across Dublin, Kildare, Meath and Wicklow online.',
    keywords: ['church live stream Dublin', 'Sunday worship live Ireland', 'Ministry of Jesus YouTube'],
  },
  morningPrayer: {
    path: '/morning-prayer',
    title: 'Early Morning Prayer (Online) | Ministry of Jesus',
    description:
      'Join Ministry of Jesus on Google Meet every Monday from 05:00 to 06:00 AM (Ireland time) for Early Morning Prayer. Open to believers across Ireland.',
    keywords: ['online morning prayer Ireland', 'Google Meet prayer Dublin', 'Monday prayer meeting Ireland'],
  },
  contact: {
    path: '/contact',
    title: 'Contact Us | Ministry of Jesus, Cherry Orchard Dublin',
    description:
      'Contact Ministry of Jesus at 7 Le Fanu Rd, Cherry Orchard, Dublin D10 NH74. Call +353 87 231 9966 or email info@ministryofjesus.ie. Pastoral care across Dublin, Kildare, Meath and Wicklow.',
    keywords: ['contact Ministry of Jesus', 'church contact Dublin', 'pastoral care Dublin'],
  },
  rsvp: {
    path: '/events/:slug/rsvp',
    title: 'Event RSVP | Ministry of Jesus, Dublin',
    description:
      'Reserve your seat for the next Ministry of Jesus event in Dublin. Open to worshippers across Dublin, Kildare, Meath and Wicklow.',
    keywords: ['church event RSVP Dublin', 'Christian events Ireland'],
    noindex: true, // Dynamic per-event pages shouldn’t be heavily indexed
  },
};

// Helper: build absolute URL safely
export function absoluteUrl(path = '/') {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}
